const colorPattern = /(?:fill|stroke|stop-color)\s*=\s*["']([^"']+)["']/gi;
const stylePattern = /style\s*=\s*["']([^"']+)["']/gi;
const styleBlockPattern = /<style[^>]*>([\s\S]*?)<\/style>/gi;
const cssColorPattern = /(?:fill|stroke|stop-color)\s*:\s*([^;]+);/gi;

const parseColorToRgba = (value) => {
  if (!value) return null;
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "black") return { r: 0, g: 0, b: 0, a: 1 };
  if (trimmed === "white") return { r: 255, g: 255, b: 255, a: 1 };

  if (trimmed.startsWith("#")) {
    const hex = trimmed.slice(1);
    const isShort = hex.length === 3 || hex.length === 4;
    const isLong = hex.length === 6 || hex.length === 8;
    if (!isShort && !isLong) return null;
    const expand = (digit) => parseInt(digit + digit, 16);
    const parseByte = (pair) => parseInt(pair, 16);

    if (isShort) {
      const r = expand(hex[0]);
      const g = expand(hex[1]);
      const b = expand(hex[2]);
      const a = hex.length === 4 ? expand(hex[3]) / 255 : 1;
      return { r, g, b, a };
    }

    const r = parseByte(hex.slice(0, 2));
    const g = parseByte(hex.slice(2, 4));
    const b = parseByte(hex.slice(4, 6));
    const a = hex.length === 8 ? parseByte(hex.slice(6, 8)) / 255 : 1;
    return { r, g, b, a };
  }

  if (trimmed.startsWith("rgb(")) {
    const values = trimmed.slice(4, -1).split(",").map((item) => item.trim());
    if (values.length < 3) return null;
    const r = parseFloat(values[0]);
    const g = parseFloat(values[1]);
    const b = parseFloat(values[2]);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return { r, g, b, a: 1 };
  }

  if (trimmed.startsWith("rgba(")) {
    const values = trimmed.slice(5, -1).split(",").map((item) => item.trim());
    if (values.length < 4) return null;
    const r = parseFloat(values[0]);
    const g = parseFloat(values[1]);
    const b = parseFloat(values[2]);
    const a = parseFloat(values[3]);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b) || Number.isNaN(a)) {
      return null;
    }
    return { r, g, b, a };
  }

  return null;
};

const isNearNeutral = (r, g, b) => {
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance <= 0.03 || luminance >= 0.97;
};

const shouldSkipColor = (value) => {
  if (!value) return true;
  const trimmed = value.trim().toLowerCase();
  if (trimmed === "none" || trimmed === "transparent" || trimmed === "currentcolor") {
    return true;
  }
  if (trimmed.startsWith("url(")) {
    return true;
  }

  const parsed = parseColorToRgba(value);
  if (parsed) {
    if (parsed.a <= 0.05) {
      return true;
    }
    if (isNearNeutral(parsed.r, parsed.g, parsed.b)) {
      return true;
    }
  }

  return false;
};

const extractColors = (svgText) => {
  const found = new Set();
  const normalized = (value) => value.trim();

  colorPattern.lastIndex = 0;
  stylePattern.lastIndex = 0;
  styleBlockPattern.lastIndex = 0;
  cssColorPattern.lastIndex = 0;

  let match = null;
  while ((match = colorPattern.exec(svgText)) !== null) {
    const value = normalized(match[1]);
    if (!shouldSkipColor(value)) {
      found.add(value);
    }
  }

  while ((match = stylePattern.exec(svgText)) !== null) {
    const styles = match[1].split(";");
    styles.forEach((rule) => {
      const [prop, value] = rule.split(":");
      if (!prop || !value) return;
      const propName = prop.trim().toLowerCase();
      if (propName !== "fill" && propName !== "stroke" && propName !== "stop-color") {
        return;
      }
      const colorValue = normalized(value);
      if (!shouldSkipColor(colorValue)) {
        found.add(colorValue);
      }
    });
  }

  while ((match = styleBlockPattern.exec(svgText)) !== null) {
    const cssText = match[1];
    let cssMatch = null;
    cssColorPattern.lastIndex = 0;
    while ((cssMatch = cssColorPattern.exec(cssText)) !== null) {
      const colorValue = normalized(cssMatch[1]);
      if (!shouldSkipColor(colorValue)) {
        found.add(colorValue);
      }
    }
  }

  return Array.from(found);
};

export { extractColors, parseColorToRgba, shouldSkipColor };
